import type { LocalizedOnMap } from 'shared/domain/issue/issueLocation';
import { isPlainObject } from 'shared/utils/object';

/**
 * Requests queued before PT-7117 in the shape the backend refuses now (422):
 * a level's `documentation.add/remove` and an issue's loose location fields.
 * Migration 52 rewrites them once; the queue rewrites again what an old worker
 * still queued after it. Strings, not enums, as in every migration: the
 * entity types and keys of a stored request must not follow later renames.
 */

const LEGACY_LOCATION_KEYS = ['positionOnMap', 'selectedLocationType', 'targetAreas', 'finalAreas'] as const;

/** What the rewrite needs to know about the levels and the issues on the device. */
export interface LegacyRequestContext {
  /** The map version a level showed: the one a location placed on it was seen on. */
  findLevelMap: (levelId: string) => LocalizedOnMap | undefined;
  findIssueLevel: (issueId: string) => string | undefined;
}

/** A queued request, as the queue stores it. */
export interface QueuedRequest {
  entityType: string;
  method: string;
  url?: string;
  data?: unknown;
}

/** How the log names an issue still being created, which has no id yet. */
const NEW_ISSUE_IN_LOG = 'new issue';

export interface LegacyRequestRewrite<TRequest extends QueuedRequest> {
  request: TRequest;
  /** An issue whose queued location could not be kept: its level has no map to place it on. */
  droppedLocationOfIssue?: string;
}

interface LegacyMapEntry {
  documentationId: string;
  versionId: string;
  deleted?: boolean;
  modifiedAt?: string;
}

function isLegacyMapEntry(entry: unknown): entry is LegacyMapEntry {
  return isPlainObject(entry) && typeof entry.documentationId === 'string' && typeof entry.versionId === 'string';
}

/** When the entry was changed; one without a readable date counts as the oldest. */
function modificationTimeOf(entry: LegacyMapEntry): number {
  const modificationTime = Date.parse(entry.modifiedAt ?? '');
  return Number.isNaN(modificationTime) ? 0 : modificationTime;
}

/**
 * The map a level showed before PT-7117, as the backend's migrations read it:
 * the newest entry that is not deleted, with the version it pinned. Entries
 * changed at the same time keep their stored order, so the pick is the same
 * on every run.
 */
export function pickLegacyLevelMap(maps: unknown): LocalizedOnMap | undefined {
  if (!Array.isArray(maps)) {
    return undefined;
  }
  const [newest] = maps
    .filter(isLegacyMapEntry)
    .filter((entry) => !entry.deleted)
    .sort((first, second) => modificationTimeOf(second) - modificationTimeOf(first));
  return newest ? { documentationId: newest.documentationId, versionId: newest.versionId } : undefined;
}

function hasLegacyLevelChange(change: unknown): boolean {
  return isPlainObject(change) && 'documentation' in change;
}

interface LegacyIssueBody {
  body: Record<string, unknown>;
  primaryData: Record<string, unknown>;
  /** Requests queued before PT-7133 keep the body itself as their data; later ones wrap it in `body`. */
  isWrapped: boolean;
}

/** The issue body of a queued request whose primary data still has a loose location. */
function findLegacyIssueBody(data: unknown): LegacyIssueBody | undefined {
  const isWrapped = isPlainObject(data) && 'body' in data;
  const body = isWrapped ? data.body : data;
  if (!isPlainObject(body) || !isPlainObject(body.primaryData) || !hasLegacyLocation(body.primaryData)) {
    return undefined;
  }
  return { body, primaryData: body.primaryData, isWrapped };
}

/** Whether the request is still in a shape the backend refuses since PT-7117. */
export function hasLegacyShape(request: QueuedRequest): boolean {
  const { entityType, data } = request;
  if (entityType === 'level' && isPlainObject(data)) {
    return (
      (Array.isArray(data.changes) && data.changes.some(hasLegacyLevelChange)) ||
      hasLegacyLevelChange(data.afterCreateLevelChangeOutDto)
    );
  }
  return entityType === 'issue' && Boolean(findLegacyIssueBody(data));
}

export function rewriteLegacyQueuedRequest<TRequest extends QueuedRequest>(
  request: TRequest,
  context: LegacyRequestContext
): LegacyRequestRewrite<TRequest> {
  if (request.entityType === 'level') {
    return { request: { ...request, data: rewriteLevelData(request.data) } };
  }
  if (request.entityType === 'issue') {
    return rewriteIssueRequest(request, context);
  }
  return { request };
}

/** `documentation.add/remove` becomes `map`: the added documentation, or none when only removed. */
function toLevelMapChange(change: unknown): unknown {
  if (!isPlainObject(change) || !isPlainObject(change.documentation)) {
    return change;
  }
  const { documentation, ...rest } = change;
  const [added] = Array.isArray(documentation.add) ? documentation.add.filter(isLegacyMapEntry) : [];
  if (added) {
    return { ...rest, map: added.documentationId };
  }
  return Array.isArray(documentation.remove) && documentation.remove.length > 0 ? { ...rest, map: null } : rest;
}

function rewriteLevelData(data: unknown): unknown {
  if (!isPlainObject(data)) {
    return data;
  }
  return {
    ...data,
    ...(Array.isArray(data.changes) ? { changes: data.changes.map(toLevelMapChange) } : {}),
    ...(data.afterCreateLevelChangeOutDto ? { afterCreateLevelChangeOutDto: toLevelMapChange(data.afterCreateLevelChangeOutDto) } : {}),
  };
}

function hasLegacyLocation(primaryData: Record<string, unknown>): boolean {
  return LEGACY_LOCATION_KEYS.some((key) => key in primaryData);
}

const ISSUE_ID_IN_URL = /issue\/([^/?]+)/;

/** The issue a request edits; `undefined` for an issue being created. */
function issueIdOf(request: QueuedRequest): string | undefined {
  const { data, url } = request;
  if (isPlainObject(data) && typeof data.issueId === 'string') {
    return data.issueId;
  }
  const [, issueId] = url?.match(ISSUE_ID_IN_URL) ?? [];
  return issueId;
}

function rewriteIssueRequest<TRequest extends QueuedRequest>(
  request: TRequest,
  context: LegacyRequestContext
): LegacyRequestRewrite<TRequest> {
  const legacyIssueBody = findLegacyIssueBody(request.data);
  if (!legacyIssueBody) {
    return { request };
  }
  const { body, isWrapped } = legacyIssueBody;
  const { positionOnMap, selectedLocationType, targetAreas, finalAreas, ...primaryData } = legacyIssueBody.primaryData;
  const issueId = issueIdOf(request);
  const levelId = typeof primaryData.level === 'string' ? primaryData.level : issueId && context.findIssueLevel(issueId);
  const localizedOnMap = levelId ? context.findLevelMap(levelId) : undefined;
  // A frozen copy of the location shape of PT-7117 (see `toIssueLocation`): a migration must not follow later changes.
  const location = localizedOnMap
    ? selectedLocationType === 'area'
      ? { selectedLocationType, targetAreas, finalAreas, localizedOnMap }
      : { selectedLocationType: selectedLocationType ?? 'pin', positionOnMap, localizedOnMap }
    : undefined;

  const rewrittenBody = { ...body, primaryData: location ? { ...primaryData, location } : primaryData };
  const data = isWrapped && isPlainObject(request.data) ? { ...request.data, body: rewrittenBody } : rewrittenBody;
  return {
    request: { ...request, data },
    ...(location ? {} : { droppedLocationOfIssue: issueId ?? NEW_ISSUE_IN_LOG }),
  };
}
